import React, { useState } from "react";
import { Calculator } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useCurrency } from "@/context/CurrencyContext";
import { rateBasisSuffix } from "@/data/rateBasis";

/**
 * Service total estimator. Lets a buyer punch in "I need N sessions" and
 * see the running total in their preferred currency + the seller's original
 * currency. Only renders for service listings with a per-unit rate basis
 * (hour/session/day/project). Fixed-price services hide it.
 *
 *   <ServiceEstimator listing={listing} />
 */
const unitNoun = {
  hour:    { sing: "hour",    plural: "hours"    },
  session: { sing: "session", plural: "sessions" },
  day:     { sing: "day",     plural: "days"     },
  project: { sing: "project", plural: "projects" },
};

const ServiceEstimator = ({ listing }) => {
  const { formatPrice, isConverted, displayCurrency } = useCurrency();
  const [units, setUnits] = useState(1);

  const basis = listing.rate_basis || "hour";
  if (listing.type !== "service" || basis === "fixed") return null;

  const n = Math.max(0, Number(units) || 0);
  const totalRaw = Number(listing.price) * n;

  const totalConverted = formatPrice(totalRaw, listing.currency);
  const totalOriginal = formatPrice(totalRaw, listing.currency, { in: listing.currency });
  const showOriginal = isConverted(listing.currency);
  const noun = unitNoun[basis] || { sing: basis, plural: basis + "s" };
  const label = n === 1 ? noun.sing : noun.plural;

  return (
    <div className="mt-6 bg-[#F3EFE9] border border-[#C05A3C]/20 rounded-lg p-4" data-testid="service-estimator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-[#C05A3C]"/>
        <div className="label-overline text-[#C05A3C]">Estimate the total</div>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{noun.plural} you need</div>
          <Input
            type="number"
            min="1"
            step="1"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            className="w-24"
            data-testid="estimator-units"
            inputMode="numeric"
          />
        </div>
        <div className="flex-1 min-w-[140px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Estimated total</div>
          <div className="font-display text-2xl text-[#1A2024]" data-testid="estimator-total">
            {totalConverted ?? totalOriginal}
          </div>
          {showOriginal && totalConverted && (
            <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="estimator-total-original">
              ≈ {totalOriginal} in {listing.currency}
            </div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-3 font-sans-ui">
        {n} {label} × {formatPrice(listing.price, listing.currency) || listing.price} {rateBasisSuffix(basis, "long")} (estimate; final price agreed with seller)
      </div>
    </div>
  );
};

export default ServiceEstimator;
